import React, { createContext, useContext, useRef, useCallback } from 'react';

interface TabIndexContextType {
  getNextTabIndex: () => number;
  resetTabIndex: () => void;
  getCurrentTabIndex: () => number;
  reserveTabIndicesForCategory: (categoryDeviceCount: number) => number;
  getTabIndexForCategoryDevice: (
    categoryStartIndex: number,
    deviceIndex: number
  ) => number;
}

const TabIndexContext = createContext<TabIndexContextType | undefined>(
  undefined
);

interface TabIndexProviderProps {
  children: React.ReactNode;
  startingIndex?: number;
}

export const TabIndexProvider: React.FC<TabIndexProviderProps> = ({
  children,
  startingIndex = 1
}) => {
  const currentTabIndexRef = useRef(startingIndex);

  const getNextTabIndex = useCallback(() => {
    const nextIndex = currentTabIndexRef.current;
    currentTabIndexRef.current += 1;
    return nextIndex;
  }, []);

  const resetTabIndex = useCallback(() => {
    currentTabIndexRef.current = startingIndex;
  }, [startingIndex]);

  const getCurrentTabIndex = useCallback(() => {
    return currentTabIndexRef.current;
  }, []);

  // Reserve a block of tab indices for a category and its devices
  // Returns the starting index for the category button
  const reserveTabIndicesForCategory = useCallback(
    (categoryDeviceCount: number) => {
      const categoryButtonIndex = currentTabIndexRef.current;
      // Reserve: 1 for category button + count for devices
      currentTabIndexRef.current += 1 + categoryDeviceCount;
      return categoryButtonIndex;
    },
    []
  );

  // Get tab index for a specific device within a category
  const getTabIndexForCategoryDevice = useCallback(
    (categoryStartIndex: number, deviceIndex: number) => {
      return categoryStartIndex + 1 + deviceIndex;
    },
    []
  );

  const value = {
    getNextTabIndex,
    resetTabIndex,
    getCurrentTabIndex,
    reserveTabIndicesForCategory,
    getTabIndexForCategoryDevice
  };

  return (
    <TabIndexContext.Provider value={value}>
      {children}
    </TabIndexContext.Provider>
  );
};

export const useTabIndex = (): TabIndexContextType => {
  const context = useContext(TabIndexContext);
  if (!context) {
    throw new Error('useTabIndex must be used within a TabIndexProvider');
  }
  return context;
};
